import styled from '@emotion/styled';
import { OverviewMapContainer } from '@resistapp/client/components/map/overview-map';
import { OverviewFocusHeader } from '@resistapp/client/components/overview-focus-header/overview-focus-header';
import { OverviewProcessModeHeader } from '@resistapp/client/components/overview-process-sites-table/overview-process-mode-header';
import { OverviewProcessSitesTable } from '@resistapp/client/components/overview-process-sites-table/overview-process-sites-table';
import { ChartContextContainer } from '@resistapp/client/components/plots/trendchart/chart-container/chart-container';
import { ArgiChart } from '@resistapp/client/components/plots/trendchart/chart/chart-types/argi-chart';
import { GeneTrendChart } from '@resistapp/client/components/plots/trendchart/chart/chart-types/gene-trend-chart';
import { ReductionChart } from '@resistapp/client/components/plots/trendchart/chart/chart-types/reduction-chart';
import { RiskScoreChart } from '@resistapp/client/components/plots/trendchart/chart/chart-types/risk-score-chart';
import { SiteDetailBoxes } from '@resistapp/client/components/selected-map-sampling-point/site-detail-boxes';
import { SiteDetailContainer } from '@resistapp/client/components/selected-map-sampling-point/site-details/general-site-details';
import { DeadCenter } from '@resistapp/client/components/shared/layout';
import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { theme } from '@resistapp/client/components/shared/theme';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { ContextualTipProvider } from '@resistapp/client/contexts/contextual-tip-context';
import { MapContextProvider } from '@resistapp/client/contexts/map-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useUrlContext } from '@resistapp/client/contexts/url-provider';
import { useChartDisplayMode } from '@resistapp/client/contexts/use-overview-context/chart-display-mode-context';
import { TrendChartProvider } from '@resistapp/client/contexts/use-overview-context/trendchart-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { Filters } from '@resistapp/client/data-utils/filter-data/filter';
import { AllProjectSampleTypesGroup, ComparableSampleGroupType } from '@resistapp/common/comparable-sample-groups';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import { ChartDisplayMode, MetricMode } from '@resistapp/common/types';
import { useMemo, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { getOverviewAccessState } from './overview-access';

interface MetricChartProps {
  metricMode: MetricMode;
  queryFilters: {
    filters: Pick<Filters, 'selectedTargets' | 'selectedTargetGrouping'>;
  };
}

export function OverviewView() {
  const { data } = useSampleDataContext();
  const {
    loading,
    error,
    metricMode,
    processGroupLevelEnabled,
    selectedSampleTypeGroup,
    selectedStepPointId,
  } = useOverviewContext();
  const { queryFilters } = useSampleDataContext();
  const { assaysLoaded } = useAssayContext();
  const { user } = useUser();
  const { page, projectId, query } = useUrlContext();
  const trendChartContainerRef = useRef<HTMLDivElement>(null);
  const reductionDisabledForProcessMode = processGroupLevelEnabled && metricMode === MetricMode.REDUCTION;
  const accessState = useMemo(
    () => getOverviewAccessState({ user, metricMode, viableProjectId: projectId.viable }),
    [metricMode, projectId.viable, user],
  );

  if (loading || !assaysLoaded || accessState === 'loading') {
    return <LoadingView />;
  } else if (error) {
    throw error;
  } else if (!data?.id) {
    return (
      <DeadCenter>
        <div>Please select projects</div>
      </DeadCenter>
    );
  } else if (accessState === 'redirect') {
    return projectId.viable ? <Navigate to={`/${page.viable}/${projectId.viable}${query}`} replace /> : null;
  } else if (reductionDisabledForProcessMode) {
    return (
      <DeadCenter>
        <div>No data available for this view in process mode</div>
      </DeadCenter>
    );
  }

  const noDataAvailableForSite =
    metricMode === MetricMode.REDUCTION &&
    selectedSampleTypeGroup !== ComparableSampleGroupType.WATER_TREATMENT &&
    selectedSampleTypeGroup !== AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS;
  const showDetailSection = !processGroupLevelEnabled || !!selectedStepPointId;

  return (
    <ContextualTipProvider>
      <OverviewContainer className={TestSelectors.TEST_OVERVIEW_CONTAINER}>
        <MapContextProvider>
          {processGroupLevelEnabled ? (
            <>
              <OverviewProcessModeHeader />
              <OverviewProcessSitesTable />
            </>
          ) : (
            <OverviewMapContainer />
          )}
          {showDetailSection && (
            <DetailContainer style={processGroupLevelEnabled ? { paddingTop: oldTheme.spacing.s } : undefined}>
              {!processGroupLevelEnabled && <OverviewFocusHeader />}
              {noDataAvailableForSite ? (
                <SiteDetailContainer>
                  <DeadCenter style={{ height: '60px' }}>No data available for this sample type</DeadCenter>
                </SiteDetailContainer>
              ) : (
                <SiteDetailBoxes />
              )}
              <ChartContextContainer>
                <div ref={trendChartContainerRef} id="trendchart-ref-container">
                  <TrendChartProvider containerRef={trendChartContainerRef}>
                    <MetricChart metricMode={metricMode} queryFilters={queryFilters} />
                  </TrendChartProvider>
                </div>
              </ChartContextContainer>
            </DetailContainer>
          )}
        </MapContextProvider>
      </OverviewContainer>
    </ContextualTipProvider>
  );
}

function MetricChart({ metricMode, queryFilters }: MetricChartProps) {
  const { chartDisplayMode } = useChartDisplayMode();
  if (chartDisplayMode === ChartDisplayMode.ABUNDANCES) {
    return <GeneTrendChart />;
  }
  switch (metricMode) {
    case MetricMode.ARGI:
      return <ArgiChart queryFilters={queryFilters} />;
    case MetricMode.RISK:
      return <RiskScoreChart />;
    case MetricMode.REDUCTION:
      return <ReductionChart />;
    default:
      return <ArgiChart queryFilters={queryFilters} />;
  }
}

export function ReductionOrRawTreatedChart() {
  const { chartDisplayMode } = useChartDisplayMode();
  return chartDisplayMode === ChartDisplayMode.METRIC ? <ReductionChart /> : <GeneTrendChart />;
}

const OverviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.neutral50};
  overflow-x: hidden;
  overflow-y: visible;
`;

const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${oldTheme.spacing.l};
  padding: ${oldTheme.spacing.l};
  padding-bottom: 0;
`;
